/**
 * Regras de preenchimento das etapas do wizard da Proposta PME — quais etapas
 * já estão completas e em qual o wizard abre.
 */

import type {
  GroupLifeFrontCustomerAddress,
  GroupLifeFrontStipulator,
  GroupLifeFrontSubStipulator,
} from '@/services/group-life';

type PmeContactInput = {
  isCaepf?: boolean;
  address?: GroupLifeFrontCustomerAddress;
  email?: string;
  phone?: string;
  genderCode?: string;
  dateOfBirth?: string;
};

const hasText = (value?: string | null) => Boolean(value?.trim());

/**
 * Mesmos obrigatórios do `validateRequired` do formulário de estipulante com
 * `requireAddressFields`: CEP, endereço, número, bairro, cidade, UF, e-mail e
 * telefone — e Sexo e Data de Nascimento quando CAEPF. A Api cobra os mesmos
 * (`group-life-proposal-completeness`).
 */
function isContactComplete(party: PmeContactInput): boolean {
  const caepfComplete =
    !party.isCaepf || (hasText(party.genderCode) && hasText(party.dateOfBirth));
  if (!caepfComplete) return false;
  const cepDigits = String(party.address?.zipCode ?? '').replace(/\D/g, '');
  return (
    cepDigits.length === 8 &&
    hasText(party.address?.street) &&
    hasText(party.address?.number) &&
    hasText(party.address?.neighborhood) &&
    hasText(party.address?.city) &&
    hasText(party.address?.state) &&
    hasText(party.email) &&
    hasText(party.phone)
  );
}

/**
 * Etapa "Dados do Seguro" completa: início de vigência + obrigatórios do
 * estipulante e de cada subestipulante.
 *
 * A cotação PME pode ser criada sem e-mail e telefone (eles só são exigidos na
 * proposta), então a data de vigência — que já vem da cotação — não basta para
 * dizer que o corretor passou por esta etapa.
 */
export function isPmeInsuranceStepComplete({
  effectiveDate,
  stipulator,
  subStipulators,
}: {
  effectiveDate?: string;
  stipulator?: GroupLifeFrontStipulator;
  subStipulators?: GroupLifeFrontSubStipulator[];
}): boolean {
  if (!hasText(effectiveDate) || !stipulator) return false;
  if (!isContactComplete(stipulator)) return false;
  return (subStipulators ?? []).every(isContactComplete);
}

/**
 * Etapa em que o wizard abre no "Editar".
 *
 * O padrão é o Resumo (é de lá que o corretor confere a proposta e segue para a
 * assinatura), mas só quando as etapas anteriores estão completas: proposta
 * recém-gerada, com Dados do Seguro ou Pagamento por preencher, abre na
 * primeira etapa pendente — senão o corretor cai num Resumo sem os dados que
 * o Confirmar exige.
 */
export function resolvePmeEditStartStep({
  stepFilled,
  resumeStepIndex,
}: {
  /** Preenchimento das etapas ANTERIORES ao Resumo, em ordem. */
  stepFilled: readonly boolean[];
  resumeStepIndex: number;
}): number {
  const firstPending = stepFilled
    .slice(0, resumeStepIndex)
    .findIndex(filled => !filled);
  return firstPending === -1 ? resumeStepIndex : firstPending;
}

/**
 * Clique direto no número da trilha de steps: só vale para uma etapa
 * preenchida cujas anteriores também estejam completas. O Plano já vem
 * preenchido da cotação, mas não pode ser alcançado pulando Dados do Seguro
 * com e-mail e telefone em branco.
 */
export function canReachPmeStep({
  stepFilled,
  index,
}: {
  stepFilled: readonly boolean[];
  index: number;
}): boolean {
  if (index < 0 || index >= stepFilled.length) return false;
  return stepFilled.slice(0, index + 1).every(Boolean);
}

/** Etapa "Pagamento" gravada: Corte e Dia de vencimento persistidos. */
export function isPmePaymentComplete(
  payment?: { billingCut?: string; dueDay?: string } | null
): boolean {
  return hasText(payment?.billingCut) && hasText(payment?.dueDay);
}
